/**
 * Admin Settings Page
 *
 * Allows Maryam (admin/designer) to customize site appearance:
 * - Color theme presets and custom colors
 * - Dark/light mode preferences
 *
 * This is admin-only - visitors see the theme Maryam chooses.
 *
 * @see Plan: Phase 5.5 - Dashboard Settings Page
 */

import { Separator } from "@designs-by-maryam/ui/components/separator";
import { createFileRoute } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { ThemePicker } from "@/components/dashboard/theme-picker";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { requireAdmin } from "@/functions/require-role";

export const Route = createFileRoute("/_dashboard/admin/settings/")({
	component: SettingsPage,
	beforeLoad: async () => {
		await requireAdmin();
	},
});

function SettingsPage() {
	return (
		<div className="mx-auto max-w-3xl space-y-8">
			{/* Page Header */}
			<div className="space-y-1">
				<div className="flex items-center gap-3">
					<Settings className="h-8 w-8 text-primary" />
					<h1 className="font-bold text-3xl tracking-tight">Site Settings</h1>
				</div>
				<p className="text-muted-foreground">
					Customize how your portfolio looks for visitors.
				</p>
			</div>

			<Separator />

			{/* Theme Section */}
			<section className="space-y-6">
				<div>
					<h2 className="font-semibold text-xl">Appearance</h2>
					<p className="text-muted-foreground text-sm">
						Personalize your site's visual style. Changes apply instantly for
						all visitors.
					</p>
				</div>

				{/* Dark Mode Toggle */}
				<div className="flex items-center justify-between rounded-lg border p-4">
					<div className="space-y-1">
						<h3 className="font-medium">Dark Mode</h3>
						<p className="text-muted-foreground text-sm">
							Toggle between light and dark themes.
						</p>
					</div>
					<ThemeToggle />
				</div>

				{/* Color Theme Picker */}
				<div className="rounded-lg border p-6">
					<ThemePicker />
				</div>
			</section>

			<Separator />

			{/* Future Settings */}
			<section className="space-y-4 opacity-50">
				<div>
					<h2 className="font-semibold text-xl">Coming Soon</h2>
					<p className="text-muted-foreground text-sm">
						More customization options will be added in future updates.
					</p>
				</div>
				<ul className="list-inside list-disc space-y-1 text-muted-foreground text-sm">
					<li>Custom fonts</li>
					<li>Layout preferences</li>
					<li>Social media links</li>
				</ul>
			</section>
		</div>
	);
}
